import React from 'react';
import axios from 'axios';

import { isIE } from '../../utils/Browser';
import Loader from '../Loader';

import './styles.css';

const defaultListOptions = {
  offset: 0,
  limit: 10,
  sort: 'name',
  order: 'asc',
};

const getData = (
  baseUrl,
  clientId = 1,
  locationSetId = -1,
  productEventId = 1,
  productNotificationId = 1,
  options = defaultListOptions,
) => {
  const url = `${baseUrl}/api/client/${clientId}/product-event/${productEventId}/product-notification/${productNotificationId}/table`;

  const newOptions = {
    ...options,
    sort: options.sort,
    // TODO: remove this when we can actually use location sets
    locationSetId,
  };
  const config = {
    params: newOptions,
  };

  return axios.get(url, config);
};

class Table extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      message: null,
      loading: true,
      initialLoad: true,
      listOptions: defaultListOptions,
      isEndOfTable: false,
      headers: [],
      rows: [],
    };

    this.rowOne = React.createRef();
    this.tableContainer = React.createRef();
    this.tableHeader = React.createRef();
    this.mountTable = this.mountTable.bind(this);
    this.renderTableRows = this.renderTableRows.bind(this);
    this.renderTableRowColumn = this.renderTableRowColumn.bind(this);
    this.addData = this.addData.bind(this);
    this.listenScrollEvent = this.listenScrollEvent.bind(this);
    this.onHeaderClick = this.onHeaderClick.bind(this);
    this.updateHeaderSize = this.updateHeaderSize.bind(this);
  }

  componentDidMount() {
    this.mountTable();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.locationSetId !== this.props.locationSetId) {
      this.mountTable();
    }
  }

  mountTable() {
    this.setState({
      ...this.state,
      isEndOfTable: false,
    }, () => {
      if (this.tableContainer.current) {
        this.tableContainer.current.addEventListener('scroll', this.listenScrollEvent);
      }
      this.addData(true);
    });
  }

  updateHeaderSize(position) {
    // if the user is using ie position: sticky is not supported so we have to create the sticky
    // effect
    if (isIE) {
      if (position !== 0) {
        this.tableHeader.current.style.position = 'absolute';
        this.tableHeader.current.style.top = `${position}px`;
      } else {
        this.tableHeader.current.style.position = 'inherit';
      }
    }

    const { childNodes } = this.tableHeader.current;
    const firstHeaderRow = childNodes[0];
    const { childNodes: rowOneCells } = this.rowOne.current;


    for (let i = 0; i < rowOneCells.length; i += 1) {
      const cell = rowOneCells[i];
      const headerCell = firstHeaderRow.childNodes[i];
      const rowCellWidth = document.defaultView.getComputedStyle(cell, null).width;
      const firstHeaderCellWidth = document.defaultView.getComputedStyle(headerCell, null).width;

      const rowCellWidthNum = Number(rowCellWidth.replace('px', ''));
      const firstHeaderCellWidthNum = Number(firstHeaderCellWidth.replace('px', ''));

      // check what element has a bigger width and then set the other element to the max width
      // of the two elements
      if (rowCellWidthNum > firstHeaderCellWidthNum) {
        headerCell.style.minWidth = rowCellWidth;
      } else {
        cell.style.minWidth = firstHeaderCellWidth;
      }
    }
  }

  listenScrollEvent(e) {
    const {
      offsetHeight,
      scrollTop,
      scrollHeight,
    } = e.target;
    const scrolledToEnd = (offsetHeight + scrollTop >= (scrollHeight - 200));
    // update the header sticky position and size
    this.updateHeaderSize(scrollTop);

    if (scrolledToEnd) {
      const {
        listOptions: oldListOptions,
      } = this.state;
      const { offset } = oldListOptions;

      const listOptions = {
        ...oldListOptions,
        offset: offset + 1,
      };

      this.setState({
        ...this.state,
        listOptions,
      }, () => this.addData(false));
    }
  }

  setLoading(loading) {
    const {
      initialLoad,
    } = this.state;

    if (initialLoad && loading === false) {
      this.setState({
        initialLoad: false,
        loading: false,
      });

      // NOTE: update the header after the first load
      // if you dont do this the headers wont be spaced right
      this.updateHeaderSize(0);
    } else {
      this.setState({
        loading,
      });
    }
  }

  addData(clearTable = false) {
    const getTheData = () => {
      const { isEndOfTable } = this.state;
      if (isEndOfTable) {
        return;
      }

      const {
        clientId,
        locationSetId,
        productEventId,
        productNotificationId,
        baseUrl,
      } = this.props;

      // show loading bubbles
      this.setLoading(true);

      const {
        offset = 0,
        limit = 10,
        sort = 'name',
        order = 'asc',
      } = this.state.listOptions;

      getData(baseUrl, clientId, locationSetId, productEventId, productNotificationId, {
        offset, limit, sort, order,
      })
        .then((response) => {
          const { data } = response;
          const {
            rowList,
            headerList,
          } = data;
          this.setState({
            ...this.state,
            message: null,
            rows: clearTable ? rowList : this.state.rows.concat(rowList),
            isEndOfTable: rowList.length === 0,
            headers: headerList,
          }, () => {
            // hide loading bubbles
            this.setLoading(false);
          });
        }, (error) => {
          const { message } = error.response.data;
          this.setState({ ...this.state, message });
        });
    };

    if (clearTable) {
      this.setState({
        ...this.state,
        listOptions: {
          ...this.state.listOptions,
          offset: 0,
        },
        isEndOfTable: false,
      }, getTheData);
    } else {
      getTheData();
    }
  }

  onHeaderClick(header) {
    const { listOptions } = this.state;
    const { sort, order } = listOptions;

    const nextOrderBy = {
      sort: header.sortKey,
      order,
    };

    if (header.sortKey === sort) {
      nextOrderBy.order = order === 'desc' ? 'asc' : 'desc';
    }

    this.setState({
      ...this.state,
      listOptions: {
        ...this.state.listOptions,
        sort: nextOrderBy.sort,
        order: nextOrderBy.order,
        offset: 0,
      },
    }, () => {
      this.addData(true);
      // you have to do this to update with the size of the carrot icon changing
      // if you change to a different header
      this.updateHeaderSize(0);
    });
  }

  formatData(type, data, userPreferences) {
    // here is our only special type we deal with right now but could be adding more later
    if (type === 'timestamp' && data !== '') {
      let format = 'MM/DD/YYYY h:mm A zz';
      if (userPreferences.userTimeFormat === 24) {
        format = 'MM/DD/YYYY HH:mm zz';
      }

      return moment(data, "MM/DD/YYYY hh:mm A zz").format(format);
    }

    return data;
  }

  renderTableRowColumn(title, data, index) {
    const className = `table-td ${index === 0 ? 'first-column' : ''}`;
    const { headers }= this.state;
    const { userPreferences } = this.props;
    const header = headers[index];
    const content = this.formatData(header.type, data, userPreferences);

    return (
      <div
        className={className}
        title={title}
        key={index}
      >
        { content }
      </div>
    );
  }

  renderTableRow(key, data, headers) {
    const locationName = data[0];
    let ref = null;
    if (key === 0) {
      ref = this.rowOne;
    }

    return (
      <div
        className="table-tr"
        key={key}
        ref={ref}
      >
        { data.map((data, index) => this.renderTableRowColumn(locationName, data, index))}
      </div>
    );
  }

  renderTableRows() {
    const { rows } = this.state;
    return rows.map((d, index) => this.renderTableRow(index, d));
  }

  renderTableHeaders() {
    const { headers, listOptions } = this.state;
    const orderByColumn = listOptions.sort;
    const orderByDirection = listOptions.order;

    return headers.map((header) => {
      const activeHeader = orderByColumn === header.sortKey;

      let className = activeHeader ? 'active-th' : '';
      className += ' table-th';
      const icon = orderByDirection === 'asc' ? 'fa-chevron-up' : 'fa-chevron-down';
      return (
        <div
          key={header.sortKey}
          className={className}
          onClick={() => this.onHeaderClick(header)}
        >
          <div className="table-th-content">
            <div>
              { header.title }
            </div>
            { activeHeader && <span className={`pull-right fa ${icon}`} />}
          </div>
        </div>
      );
    });
  }

  render() {
    const tableComponent = (
      <div
        ref={this.table}
        className="table table-bordered table-sm table-hover custom-table"
      >
        <div
          ref={this.tableHeader}
          className="custom-table-thead"
        >
          <div className="table-thead">
            { this.renderTableHeaders() }
          </div>
        </div>

        <div
          className="table-body"
        >
          { this.renderTableRows() }
        </div>
      </div>
    );
    const loader = <Loader />;

    const { initialLoad, loading, message } = this.state;

    const tableLoadingComponent = initialLoad && loading ? loader : tableComponent;
    return (
      <div
        ref={this.tableContainer}
        className="table-container scroll row"
      >
        { tableComponent }
      </div>
    );
  }
}

export default Table;
